import React from 'react'
import { useParams, Link, useLocation } from 'react-router-dom'
import { getProvider } from '../../content/providerFactory'
import { getSourceById } from '../../content/registry'
import MarkdownRenderer from '../../components/MarkdownRenderer'
import DocViewer from './DocViewer'

export default function ContentViewer() {
  const params = useParams()
  const sourceId = params.source as string
  const rest = (params['*'] as string) || ''
  const location = useLocation()

  const provider = React.useMemo(() => getProvider(sourceId), [sourceId])
  const source = React.useMemo(() => getSourceById(sourceId), [sourceId])

  const [md, setMd] = React.useState<string>('')
  const [sidebar, setSidebar] = React.useState<string>('')
  const [viewUrl, setViewUrl] = React.useState<string | undefined>(undefined)
  const [error, setError] = React.useState<string>('')

  // Close the sidebar if navigating to another section; currently static but kept for parity
  React.useEffect(() => {
    // no-op for now
  }, [location.pathname])

  React.useEffect(() => {
    setMd('')
    setError('')
    setViewUrl(undefined)
    if (!provider) {
      // Delegate unknown sources to generic DocViewer using catalog/wiki fallback.
      return
    }
    const slug = rest || provider.defaultPage || 'README'
    ;(async () => {
      try {
        const result = await provider.fetchDoc(slug)
        setMd(result.md)
        setViewUrl(result.viewUrl)
      } catch (e: any) {
        setError(e?.message || 'Failed to load document')
      }
    })()
  }, [provider, rest])

  React.useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!provider?.fetchSidebar) return
      try {
        const sb = await provider.fetchSidebar()
        if (!cancelled && sb) setSidebar(sb)
      } catch (_e) {
        // ignore
      }
    })()
    return () => {
      cancelled = true
    }
  }, [provider])

  const decideLink = React.useCallback(
    (href: string) => {
      if (!provider?.rewriteLink) return undefined
      return provider.rewriteLink(href)
    },
    [provider]
  )

  // If sourceId does not map to a known provider, use DocViewer, which
  // resolves catalog entries and wiki pages by slug.
  if (!provider) {
    const combinedSlug = rest ? `${sourceId}/${rest}` : sourceId
    return <DocViewer overrideSlug={combinedSlug}/>
  }

  return (
    <section className="section api-doc-page">
      <div className="docs-layout">
        {sidebar && (
          <aside className="docs-sidebar">
            <div className="docs-sidebar-inner">
              <div className="prose">
                <MarkdownRenderer markdown={sidebar} decideLink={decideLink}/>
              </div>
            </div>
          </aside>
        )}
        <div className="docs-content">
          <div className="back-row">
            <Link to="/docs" className="back-link">← Back to docs</Link>
          </div>
          <h1 className="page-title">{source?.title || sourceId}</h1>
          {viewUrl && (
            <a
              href={viewUrl}
              target="_blank"
              rel="noreferrer"
              className="view-source-fab"
              aria-label="View on source"
              title="View on source"
            >
              {/* GitHub mark */}
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M12 2C6.48 2 2 6.58 2 12.26c0 4.5 2.87 8.32 6.86 9.67.5.1.68-.22.68-.49 0-.24-.01-.88-.01-1.73-2.79.62-3.38-1.37-3.38-1.37-.45-1.18-1.1-1.5-1.1-1.5-.9-.63.07-.62.07-.62 1 .07 1.53 1.06 1.53 1.06.89 1.57 2.34 1.12 2.9.86.09-.66.35-1.11.63-1.37-2.23-.26-4.57-1.14-4.57-5.07 0-1.12.39-2.03 1.03-2.75-.1-.26-.45-1.31.1-2.73 0 0 .84-.27 2.75 1.05.8-.23 1.65-.35 2.5-.35s1.7.12 2.5.35c1.9-1.32 2.74-1.05 2.74-1.05.55 1.42.2 2.47.1 2.73.64.72 1.02 1.63 1.02 2.75 0 3.94-2.35 4.8-4.59 5.06.36.31.68.92.68 1.86 0 1.34-.01 2.41-.01 2.74 0 .27.18.6.69.49A10.03 10.03 0 0 0 22 12.26C22 6.58 17.52 2 12 2z"
                />
              </svg>
            </a>
          )}
          {error && (
            <div className="card" role="alert">
              <strong>Failed to load.</strong> {error}
              {viewUrl && (
                <>
                  {' '}
                  <a href={viewUrl} target="_blank" rel="noreferrer">
                    Open on source
                  </a>
                </>
              )}
            </div>
          )}
          {!error && (
            <>
              <div className="prose">
                <MarkdownRenderer markdown={md} decideLink={decideLink}/>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  )
}
